import * as _ from 'lodash';
import Configuration from '../dispatcher/config/Configuration';

type STORAGE_TYPE = 'localStorage' | 'sessionStorage'

class LocalCacheCls {
  private static _instance: LocalCacheCls = null;

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }

  /**
   * Application context path
   * Defaults to /
   */
  public contextPath: string = '/';

  private constructor() {}

  /**
   * Private set function
   * @param {any} cache
   * @param {STORAGE_TYPE} storageType
   * @private
   */
  private _set(cache, storageType: STORAGE_TYPE = 'localStorage') {
    window[storageType].setItem(this._getKey(), JSON.stringify(cache));
  }

  /**
   * Returns local store cache key
   * @returns {string}
   */
  private _getKey() {
    const { REST_SERVER } = Configuration.get();
    const { NAME = '' } = REST_SERVER;
    let path = NAME.toLocaleLowerCase().replace('/', '') || 'template';
    return `${path}:${window.location.origin}`;
  }

  /**
   * Returns local cache item value
   * @param {string} itemKey
   * @param {STORAGE_TYPE} storageType
   */
  getItem(itemKey, storageType: STORAGE_TYPE = 'localStorage') {
    return this.get(storageType)[itemKey];
  }

  /**
   * Returns local store cache object
   * @param {STORAGE_TYPE} storageType
   * @returns {object}
   */
  get(storageType: STORAGE_TYPE = 'localStorage') {
    let localCache = window[storageType].getItem(this._getKey());
    try {
      return localCache ? JSON.parse(localCache) : {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Returns localStorage item
   * @param {string} key
   */
  getExternal(key) {
    return localStorage.getItem(key);
  }

  /**
   * Sets local store cache object
   * @param {object} cache
   * @param {string} storageType
   */
  set(cache, storageType: STORAGE_TYPE = 'localStorage') {
    let localCache = this.get(storageType);
    try {
      _.merge(localCache, cache);
      this._set(localCache, storageType);
    } catch (e) {}
  }

  /**
   * Sets local cache item
   * @param {string} itemKey
   * @param {string|number} item
   * @param {STORAGE_TYPE} storageType
   */
  setItem(itemKey, item, storageType: STORAGE_TYPE = 'localStorage') {
    let cache = {};
    cache[itemKey] = item;
    this.set(cache, storageType);
  }

  /**
   * Removes local cache items
   * @param {string[]} keys
   * @param {STORAGE_TYPE} storageType
   */
  remove(keys: string[], storageType: STORAGE_TYPE = 'localStorage') {
    let localCache = this.get();
    let index = keys.length;
    while (index) {
      index--;
      localCache = _.omit(localCache, keys[index]);
    }

    this._set(localCache, storageType);
  }
}

const LocalCache = LocalCacheCls.Instance;
// <debug>
if (!_.get(window, 'LocalCache')) {
  _.set(window, 'LocalCache', LocalCache);
}
// </debug>
export default LocalCache;
